<template>
  <li v-if="!isHidden" class="singleRow">
    <div
      class="title"
      :class="getClickableClass(changeOrder)"
      @click="navigateToDetailsPage(changeOrder.ChangeOrderId)"
    >
      <strong>{{ modelTrimLine }}</strong
      ><br />
      <span class="metadata">{{ formattedDate }}</span
      ><br />
      <small class="metadata">{{ carDescription }}</small>
      <br />
      <small class="metadata">
        {{ i18n.t('/configuration/komnumber') }}: {{ changeOrder.KomNumber }}
      </small>
    </div>
    <div
      class="salesemployee"
      :class="getClickableClass(changeOrder)"
      @click="navigateToDetailsPage(changeOrder.ChangeOrderId)"
    >
      {{ changeOrder.Employee.EmployeeName }}
    </div>
    <div
      class="status"
      :class="getClickableClass(changeOrder)"
      @click="navigateToDetailsPage(changeOrder.ChangeOrderId)"
    >
      <span>{{ getOrderStatus }}</span>
      <span v-if="changeOrderSigningStatusLabel">{{ changeOrderSigningStatusLabel }}</span>
      <small class="metadata">
        {{ i18n.t('/change-order/dashboard/change-order') }}
        <strong>{{ changeOrder.ChangeOrderId }}</strong>
      </small>
    </div>
    <div class="actions">
      <app-button
        button-class="icon-stack"
        data-qa="change-order-result-button"
        :disabled="!hasValidationMessages"
        @click="toggleShowOrderModificationResult(true, $event)"
      >
        <span class="btn-icon">&#x1F4C4;</span>
        <span class="btn-label">result</span>
      </app-button>
      <app-button
        button-class="icon-stack"
        data-qa="change-order-remark-button"
        :disabled="!hasRemark"
        @click="toggleRemarkModal(true, $event)"
      >
        <span class="btn-icon">&#x2139;</span>
        <span class="btn-label">info</span>
      </app-button>
      <app-button
        button-class="icon-stack"
        data-qa="change-order-delete-button"
        @click="toggleDeleteConfirmModal(true, $event)"
      >
        <span class="btn-icon">&#x1F5D1;</span>
        <span class="btn-label">archief</span>
      </app-button>
      <app-button
        v-if="changeOrder.Type === 'SINGLE'"
        button-class="icon-stack"
        data-qa="change-order-audit-trail-button"
        @click="toggleAuditTrailModal(true, $event)"
      >
        <span class="btn-icon">&#x1F4CB;</span>
        <span class="btn-label">log</span>
      </app-button>
    </div>

    <modal
      v-if="showDeleteConfirmModal"
      :title="i18n.t('/Common/Confirmation')"
      @close="toggleDeleteConfirmModal(false, $event)"
    >
      <p>
        {{ i18n.t('/change-order/dashboard/delete-confirm') }}
      </p>
      <app-button
        data-qa="delete-change-order-cancel-button"
        @click="toggleDeleteConfirmModal(false, $event)"
        >{{ i18n.t('/Common/Cancel') }}</app-button
      >
      <app-button data-qa="delete-change-order-confirm-button" @click="confirmDelete">{{
        i18n.t('/Common/Delete')
      }}</app-button>
    </modal>

    <modal
      v-if="showRemarkModal"
      :title="i18n.t('/Dashboard/RemarkModalTitle')"
      @close="toggleRemarkModal(false, $event)"
    >
      <p>
        {{ changeOrder.CarConfiguration.Remark }}
      </p>
    </modal>

    <change-order-modification-result
      v-if="showOrderModificationResult"
      :order-result="changeOrder.ValidationMessages"
      @close="toggleShowOrderModificationResult(false)"
    />

    <bulk-change-order-excel-result
      v-if="showBulkExcelResult && bulkExcelResultData"
      :excel-result="bulkExcelResultData"
      @close="closeBulkExcelResult"
    />

    <audit-trail-modal
      v-if="showAuditTrailModal"
      :change-order-id="changeOrder.ChangeOrderId"
      :change-order-title="modelTrimLine"
      @close="toggleAuditTrailModal(false)"
    />
  </li>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import changeOrderService from '@/services/changeOrderService.js'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'
import ChangeOrderModificationResult from '@/components/changeOrder/presentational/organisms/changeOrderActions/ChangeOrderModificationResult.vue'
import BulkChangeOrderExcelResult from '@/components/changeOrder/presentational/organisms/changeOrderActions/BulkChangeOrderExcelResult.vue'
import AuditTrailModal from '@/components/changeOrder/presentational/molecules/auditTrail/AuditTrailModal.vue'

export default {
  components: {
    ChangeOrderModificationResult,
    BulkChangeOrderExcelResult,
    AuditTrailModal,
    Modal,
    AppButton,
  },
  props: {
    changeOrder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showDeleteConfirmModal: false,
      showRemarkModal: false,
      isHidden: false,
      showOrderModificationResult: false,
      showBulkExcelResult: false,
      bulkExcelResultData: null,
      isLoadingBulkResult: false,
      showAuditTrailModal: false,
    }
  },
  computed: {
    modelTrimLine() {
      if (this.changeOrder.Type === 'SINGLE') {
        return (
          this.changeOrder.CarConfiguration.ModelName +
          ' ' +
          this.changeOrder.CarConfiguration.TrimlineName
        )
      }
      return 'Gewijzigd via bulk wijziging'
    },
    formattedDate() {
      return this.changeOrder.CreateDate
    },
    hasRemark() {
      return (
        this.changeOrder?.CarConfiguration?.Remark &&
        this.changeOrder.CarConfiguration.Remark !== ''
      )
    },
    hasValidationMessages() {
      return this.changeOrder.ValidationMessages
    },
    carColor() {
      let color = this.changeOrder.CarConfiguration.OriginalOptions?.find(
        (option) => option.Type === 'EXTERIOR_COLOR'
      )
      if (color) {
        return color.Name
      }
      return ''
    },
    getOrderStatus() {
      switch (this.changeOrder.MainOrderStatus) {
        case ChangeOrderStatus.concept:
          return ChangeOrderStatus.concept
        case ChangeOrderStatus.submitted:
          return 'Afgehandeld'
        case ChangeOrderStatus.archived:
          return 'Gearchiveerd'
        default:
          return 'Onbekend'
      }
    },
    carDescription() {
      if (this.changeOrder.Type === 'SINGLE') {
        return this.changeOrder.CarConfiguration.CatalogCarName + ' (' + this.carColor + ')'
      }
      return ''
    },
    changeOrderSigningStatusLabel() {
      // Don't show signing status if order is already submitted
      if (this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted) {
        return null
      }

      if (!this.changeOrder.SigningStatus) return null

      switch (this.changeOrder.SigningStatus) {
        case digitalSigningStatus.openedByCustomer:
          return this.i18n.t(`/dashboard/signingstatus/openedByCustomer`)
        case digitalSigningStatus.manualSignCompleted:
          return this.i18n.t(`/dashboard/signingstatus/signManuallyCompleted`)
        case digitalSigningStatus.signCompleted:
          return this.i18n.t(`/dashboard/signingstatus/signCompleted`)
        case digitalSigningStatus.manualSignPending:
          return this.i18n.t(`/dashboard/signingstatus/manualSignPending`)
        case digitalSigningStatus.sentToCustomer:
          return this.i18n.t(`/dashboard/signingstatus/sentToCustomer`)
        case digitalSigningStatus.signedByCustomer:
        case digitalSigningStatus.documentSignedByCustomer:
          return this.i18n.t(`/dashboard/signingstatus/signedByCustomer`)
        default:
          return null
      }
    },
  },
  methods: {
    getClickableClass(changeOrder) {
      if (changeOrder.Type === 'SINGLE' || changeOrder.Type === 'BULK') {
        return 'clickable'
      }
      return ''
    },
    async navigateToDetailsPage(changeOrderId) {
      if (this.changeOrder.Type === 'SINGLE') {
        let params = {
          ...this.$route.params,
          changeOrderId: changeOrderId,
          establishmentId: this.$route.params.establishmentId,
        }

        this.$router.push({ name: 'change-order-page', params })
      } else if (this.changeOrder.Type === 'BULK') {
        // For BULK, fetch and show the excel result
        await this.fetchAndShowBulkExcelResult(changeOrderId)
      }
    },
    toggleRemarkModal(showModal, e) {
      e.stopPropagation()
      if (this.hasRemark) this.showRemarkModal = showModal
    },
    toggleShowOrderModificationResult(showModal) {
      if (this.hasValidationMessages) {
        this.showOrderModificationResult = showModal
      }
    },
    toggleDeleteConfirmModal(showModal) {
      this.showDeleteConfirmModal = showModal
    },
    confirmDelete(e) {
      e.stopPropagation()
      changeOrderService.deleteChangeOrder(this.changeOrder.ChangeOrderId)
      this.isHidden = true
    },
    async fetchAndShowBulkExcelResult(changeOrderId) {
      try {
        this.isLoadingBulkResult = true
        this.bulkExcelResultData = await changeOrderService.getChangeOrderExcelResult(changeOrderId)
        this.showBulkExcelResult = true
      } catch (error) {
        console.error('Error fetching bulk excel result:', error)
        // Error notification is already handled in the service
      } finally {
        this.isLoadingBulkResult = false
      }
    },
    closeBulkExcelResult() {
      this.showBulkExcelResult = false
      this.bulkExcelResultData = null
    },
    toggleAuditTrailModal(showModal, e) {
      if (e) e.stopPropagation()
      this.showAuditTrailModal = showModal
    },
  },
}
</script>

<style lang="scss" scoped>
.metadata {
  color: #a3b8c0;
}
.metadata-container {
  display: flex;
  justify-content: space-between;
}
.actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.title,
.customer,
.salesemployee {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.status {
  display: flex;
  flex-direction: column;
}

.clickable {
  cursor: pointer;
}
</style>
